import { Fragment, useEffect, useState } from "react"
import { BaseTable, Card, FooterMenu, HeaderMenu, Loading } from "components"
import { FormProvider, useForm } from "react-hook-form"
import { useApi, useToggle } from "utils"
import { 
  bugetMapperPayloadMapper, 
  calculateParent, 
  formatedPeriode, 
  generateNetProfit, 
  // handleExportExcelBugetManager, 
  mergeDataWithBudget, 
  // useExportPdf 
} from "./utils"
import moment from "moment"
import { ActualsEnum, CoaNameEnum, PeriodeEnum } from "./enum"
import { TableBody, TableTotalCOA, FilterBugetManager } from "./components"
import { 
  BSDataWithHistory, 
  BugetManagerListSchema, 
  BugetManagerMergeListSchema, 
  BugetManagerPayload, 
  DataBudget, 
  BugetManagerFilter
} from "./types"
import toast from "react-hot-toast"
import { PERMISION } from "security"

export default function BugetManagerListPage() {
  const api = useApi()
  // const auth = useHandleAuth()
  // const token = auth.userTokenData()
  const toggleZeroValue = useToggle(true)
  const toggleAccountNumber = useToggle(true)
  const toggleSubHeader = useToggle(true)
  const toggleDetail = useToggle(true)

  const [budgetInputs, setBudgetInputs] = useState<BugetManagerPayload[]>([])
  const [comparisonPNL, setComparisonPNL] = useState<string[]>([])
  const [dataBudget, setDataBudget] = useState<DataBudget[]>([])
  const [dataPNL, setDataPNL] = useState<BSDataWithHistory[]>([])

  const defaultValues: BugetManagerFilter = {
    range: moment().startOf("year").format("YYYY-MM-DD"),
    actuals: ActualsEnum.ThreeMonth,
    periods: PeriodeEnum.TwelveMonth,
    accountGroup: "0"
  }
  const methods = useForm<BugetManagerFilter>({ defaultValues: defaultValues })
  const isFetching = methods.formState.isSubmitting

  const nonZeropnlData: BSDataWithHistory[] = dataPNL.filter((item) => item.balance.some((balance) => balance !== 0))
  const data = toggleZeroValue.isActive ? dataPNL : nonZeropnlData

  const getDataBudget: BugetManagerMergeListSchema[] = mergeDataWithBudget(data, dataBudget)

  // const handleExport = () => handleExportExcelBugetManager({
  //   comparisonDates: comparisonPNL,
  //   data,
  //   companyName: token?.company_name ?? "",
  //   date: methods.getValues().range
  // })
  // const exportPdf = useExportPdf({ data: getDataBudget, comparisonDates: comparisonPNL })

  const parsedDates = comparisonPNL?.map(d => {
    const [month, year] = d.split("/").map(Number);
    return new Date(year, month - 1, 1);
  });
  const minDate = new Date(Math.min(...parsedDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...parsedDates.map(d => d.getTime())));
  const startDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const endDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);

  const onFilterSubmit = async (data: BugetManagerFilter) => {
    const isFilterAll = Boolean(data.accountGroup === "0")
    setComparisonPNL([])
  
    await api.postForm<{ payload: BugetManagerListSchema }>("/budgetmanager/get_data", bugetMapperPayloadMapper(data))
    .then((res) => {
      const payload = res?.data?.payload
      const flattenPNL = payload?.data_pnl?.flat()

      const dateCompare = flattenPNL?.map(({ DateStart }) => `${moment(DateStart)?.format("MM/YYYY")}`)
      const calculate = flattenPNL?.map((data) => { return { ...data, BSData: calculateParent(data?.BSData)}})

      const filterByCoaGrup = isFilterAll
        ? calculate.map((row) => ({ ...row, BSData: generateNetProfit(row.BSData)}))
        : calculate.map((row) => ({ ...row, BSData: row.BSData.filter((coa) => coa.group_coa_code === data.accountGroup)}))

      const mapBalanceToArray = filterByCoaGrup[0].BSData?.map((data) => ({ ...data, balance: filterByCoaGrup.map(({ BSData }) => { return (BSData?.find(({ coa_id }) => coa_id === data.coa_id)?.balance || 0) })}))

      setComparisonPNL(dateCompare)

      setDataPNL(mapBalanceToArray)
      setDataBudget(payload?.data_budget)
    })
  }
  const onBugetManagerSubmit = () => {
    return new Promise<void>((resolve) => {
      toast.promise(api.post("/budgetmanager/save", budgetInputs), {
        loading: "Loading...",
        success: (res) => res.data.message,
        error: (err) => err.response.data.message
      })
      .then(() => {
        methods.handleSubmit((data) => onFilterSubmit(data))()
        setBudgetInputs([])
      })
      .catch(() => setBudgetInputs([]))
      .finally(resolve)
    })
  }

  useEffect(() => {
    if (methods.formState.submitCount === 0) {
      methods.handleSubmit((data) => onFilterSubmit(data))()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Fragment>
      <section className="container my-5">
        <HeaderMenu title="BUDGET MANAGER" />

        <FormProvider {...methods}>
          <FilterBugetManager
            toggleDetail={toggleDetail}
            toggleSubHeader={toggleSubHeader}
            toggleZeroValue={toggleZeroValue}
            toggleAccountNumber={toggleAccountNumber}
            onFilterSubmit={onFilterSubmit} 
          />
        </FormProvider>

        <section className="mt-2">
          <Card className="min-h-[calc(100vh-700px)]">
            <Card.Body>
              <section className="my-5 container">
                <div className="mb-3 font-bold">
                  <div>PT HAWK</div>
                  <div className="capitalize">
                    {isFetching ? "-" : `${moment(startDate).format("DD MMMM YYYY")} - ${moment(endDate).format("DD MMMM YYYY")}`}
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[calc(100vh-700px)]">
                  <BaseTable className="border-spacing-0">
                    {comparisonPNL?.length && <thead className="border-none">
                      <tr className="border-none dark:bg-[#1d232a]">
                        <th className="border-none bg-transparent"></th>
                        <th colSpan={comparisonPNL?.length || 0} className="border-none bg-transparent text-center relative">
                          <div className="flex items-center justify-center gap-2">
                            <span className="flex-1 h-px bg-gray-400 dark:text-white" />
                            <span className="whitespace-nowrap text-sm font-semibold text-gray-600 dark:text-white">
                              Historical Actual
                            </span>
                            <span className="flex-1 h-px bg-gray-400 dark:text-white" />
                          </div>
                        </th>
                        <th className="border-none bg-transparent"></th>
                      </tr>
                    </thead>}
                    <thead>
                      <tr className="uppercase bg-black text-white">
                        <th>CHART OF ACCOUNT</th>
                        {comparisonPNL?.map((date) => (
                          <th className="text-center" key={date}>
                            {date || ""}
                          </th>
                        ))}
                        {Object?.keys(getDataBudget?.[0]?.period_balance || {})?.map((period) => (
                          <th key={period}>{formatedPeriode(period)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {!dataPNL?.length || isFetching ? (
                        <tr>
                          <td colSpan={10} className="text-center">
                            <Loading loading={isFetching} errorText={isFetching ? "" : "No data available"} />
                          </td>
                        </tr>
                      ) : (
                        getDataBudget.map((item, _key: number) => item?.parent_id === -1 && (
                          <>
                            <TableBody
                              key={item?.coa_id}
                              list={getDataBudget}
                              item={item}
                              displayAccountNumber={toggleAccountNumber.isActive}
                              displaySubHeader={toggleSubHeader.isActive}
                              displayDetail={toggleDetail.isActive}
                              comparisonPNL={comparisonPNL}
                              budgetInputs={budgetInputs}
                              setBudgetInputs={setBudgetInputs}
                            />

                            {item?.coa_name !== CoaNameEnum.NET_PROFIT && (<TableTotalCOA item={item} list={getDataBudget} />)}
                          </>
                          )
                        )
                      )}
                    </tbody>
                  </BaseTable>
                </div>
              </section>
            </Card.Body>
          </Card>
        </section>

        <div className="mt-2">
          <FooterMenu
            // export={{ actiontype: "export", onClick: handleExport }}
            save={{
              permission: PERMISION.ACCOUNTS.REPORT.BUDGET_MANAGER.UPDATE,
              actiontype: "save",
              type: "button",
              color: "primary",
              loading: methods.formState.isSubmitting ? "true" : undefined,
              onClick: onBugetManagerSubmit
            }}
            // pdf={{
            //   actiontype: "pdf",
            //   onClick: () => exportPdf.handleExportPdf({
            //     date: methods.getValues().range,
            //     displayAccountNumber: false
            //   })
            // }}
          />
        </div>
      </section>
    </Fragment>
  )
}
